<template>
  <el-dialog
    :model-value="visible"
    width="820px"
    top="80px"
    append-to-body
    class="cd-dialog"
    :show-close="false"
    @close="close"
  >
    <template #header>
      <div class="cd-header">
        <span>Case Details #{{ doc?.caseId || caseId }}</span>
        <el-button class="cd-close" type="primary" text @click="close">
          <el-icon><Close /></el-icon>Close
        </el-button>
      </div>
    </template>

    <div class="cd-body" v-loading="loading">
      <el-alert
        v-if="error"
        :title="error"
        type="error"
        show-icon
        class="mb-3"
      />

      <el-descriptions class="cd-desc" :column="2" border>
        <el-descriptions-item label="Created By">{{ doc?.createdByEmail || doc?.createdBy || 'Not available' }}</el-descriptions-item>
        <el-descriptions-item label="Status">{{ doc?.status || 'Not available' }}</el-descriptions-item>
        <el-descriptions-item label="Submitted At">{{ formatTs(doc?.submittedAt) }}</el-descriptions-item>
        <el-descriptions-item label="Updated At">{{ formatTs(doc?.updatedAt) }}</el-descriptions-item>
        <el-descriptions-item label="Assigned To">{{ doc?.assignedToName || doc?.assignedToEmail || 'Pending assignment' }}</el-descriptions-item>
        <el-descriptions-item label="Assigned At">{{ formatTs(doc?.assignedAt) }}</el-descriptions-item>
        <el-descriptions-item label="Submission Type">{{ doc?.submission?.submissionType || doc?.submissionType || 'Not available' }}</el-descriptions-item>
        <el-descriptions-item label="Protocol Title" :span="2">{{ doc?.submission?.protocolTitle || doc?.protocolTitle || 'Not available' }}</el-descriptions-item>
        <el-descriptions-item label="Submission Name" :span="2">{{ doc?.submission?.submissionName || doc?.submissionName || 'Not available' }}</el-descriptions-item>
        <el-descriptions-item label="Protocol #">{{ doc?.submission?.protocolNumber || doc?.protocolNumber || 'Not available' }}</el-descriptions-item>
        <el-descriptions-item label="Financial Disclosure">{{ doc?.submission?.hasFinancialDisclosure || doc?.hasFinancialDisclosure || 'Not available' }}</el-descriptions-item>
        <el-descriptions-item label="Sponsor">{{ doc?.submission?.sponsor || doc?.sponsor || 'Not available' }}</el-descriptions-item>
        <el-descriptions-item label="Submit PI?">{{ doc?.submission?.submitPI || doc?.submitPI || 'Not available' }}</el-descriptions-item>
      </el-descriptions>

      <el-divider class="cd-divider">
        <el-icon><DocumentCopy /></el-icon>&nbsp;Attached Files
      </el-divider>

      <div v-if="doc?.files && doc.files.length" class="files-compact">
        <el-card
          v-for="file in doc.files"
          :key="file.id"
          shadow="never"
          class="file-row"
          :body-style="{ padding: '12px 14px' }"
        >
          <div class="file-left">
            <div class="file-name-compact">{{ file.name }}</div>
            <div class="file-meta-compact">{{ prettySize(file.size) }}</div>
          </div>

          <div class="file-actions">
            <el-link
              v-if="file.downloadURL"
              class="dl-link"
              :href="file.downloadURL"
              target="_blank"
              rel="noopener"
              :download="file.name"
            >
              <el-icon><Download /></el-icon>&nbsp;
            </el-link>
            <el-button
              v-else
              class="dl-btn"
              type="primary"
              text
              size="small"
              @click="downloadFileFallback(file)"
            >
              <el-icon><Download /></el-icon>
            </el-button>
          </div>
        </el-card>
      </div>

      <el-empty v-else description="No files" :image-size="100" style="padding:10px" />
    </div>
  </el-dialog>
</template>

<script>
import { collection, doc, getDoc, getDocs } from 'firebase/firestore'
import { ref as sRef, getDownloadURL } from 'firebase/storage'
import { db, storage } from '@/firebase'

export default {
  name: 'CaseDetailsDialog',
  props: {
    caseId: { type: [String, null], default: null },
    initialCase: { type: Object, default: null },
    visible: { type: Boolean, default: false },
  },
  data() {
    return {
      doc: null,
      loading: false,
      error: null,
    }
  },
  mounted() {
    if (this.visible && this.caseId) this.load()
  },
  watch: {
    visible(value) {
      if (value) this.load()
    },
    caseId() {
      if (this.visible) this.load()
    },
    initialCase() {
      if (this.visible) this.load()
    },
  },
  methods: {
    cloneInitialCase() {
      if (!this.initialCase) return null
      return {
        ...this.initialCase,
        files: Array.isArray(this.initialCase.files) ? [...this.initialCase.files] : [],
      }
    },
    close() {
      this.$emit('close')
    },
    async load() {
      if (!this.caseId) return
      this.loading = true
      this.error = null
      this.doc = this.cloneInitialCase()

      try {
        const caseRef = doc(db, 'cases', this.caseId)
        const snap = await getDoc(caseRef)
        if (snap.exists()) {
          this.doc = {
            ...(this.doc || {}),
            id: snap.id,
            ...snap.data(),
          }
        } else if (!this.doc) {
          this.error = 'Case not found'
        }

        if (this.doc) {
          const filesSnap = await getDocs(collection(caseRef, 'files'))
          this.doc.files = filesSnap.docs.map((fileSnap) => ({ id: fileSnap.id, ...fileSnap.data() }))
        }
      } catch (error) {
        if (!this.doc) {
          this.error = error?.message || String(error)
        } else if (!Array.isArray(this.doc.files)) {
          this.doc.files = []
        }
      } finally {
        this.loading = false
      }
    },
    formatTs(ts) {
      if (!ts) return 'Not available'
      try {
        if (ts.toDate) return ts.toDate().toLocaleString()
      } catch (error) {}
      try {
        return new Date(ts).toLocaleString()
      } catch (error) {
        return String(ts)
      }
    },
    prettySize(bytes) {
      if (bytes === 0) return '0 B'
      if (!bytes) return 'Not available'
      if (bytes < 1024) return `${bytes} B`
      const kb = bytes / 1024
      if (kb < 1024) return `${kb.toFixed(1)} KB`
      const mb = kb / 1024
      return `${mb.toFixed(2)} MB`
    },
    async downloadFileFallback(file) {
      try {
        let url = file.downloadURL || file.url || null
        if (!url && file.storagePath) {
          url = await getDownloadURL(sRef(storage, file.storagePath))
        }
        if (!url) throw new Error('File has no storage path or URL on record.')

        const link = document.createElement('a')
        link.href = url
        link.target = '_blank'
        link.rel = 'noopener'
        document.body.appendChild(link)
        link.click()
        link.remove()
      } catch (error) {
        const message =
          error?.code === 'storage/object-not-found'
            ? 'File not found in Storage.'
            : error?.code === 'storage/unauthorized' || error?.code === 'storage/permission-denied'
              ? 'You do not have permission to download this file.'
              : error?.message || 'Unable to download file.'
        alert(message)
      }
    },
  },
}
</script>

<style scoped>
.cd-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 18px;
  background: #003831;
  color: #fffbf5;
  font-weight: 800;
  letter-spacing: 0.2px;
}

.cd-close {
  color: #fffbf5 !important;
  font-weight: 700;
  opacity: 0.95;
}

.cd-close:hover {
  background-color: #018575 !important;
  opacity: 1;
}

.cd-body {
  max-height: 70vh;
  max-width: 1000px;
  overflow: auto;
  background: #fffbf5;
}

.cd-desc :deep(.el-descriptions__header) {
  margin-bottom: 10px;
}

.cd-desc :deep(.el-descriptions__body) {
  background: #fffbf5;
}

.cd-desc :deep(.el-descriptions__content),
.cd-desc :deep(.el-descriptions__label) {
  white-space: normal !important;
  overflow-wrap: anywhere;
}

.cd-desc :deep(.el-descriptions__label) {
  color: #003831;
  font-weight: 800;
  background: rgba(0, 56, 49, 0.06);
}

.cd-desc :deep(.el-descriptions__content) {
  color: #1f2a25;
  font-weight: 600;
}

.cd-divider :deep(.el-divider__text) {
  color: #003831;
  font-weight: 500;
}

.files-compact {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.file-row {
  align-self: center;
  width: 99%;
  min-height: 70px;
  border: 1px solid rgba(0, 56, 49, 0.14);
  border-radius: 14px;
  background: #fffbf5;
  transition: transform 0.12s ease, box-shadow 0.12s ease;
}

.file-row:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 26px rgba(0, 0, 0, 0.08);
}

.file-row :deep(.el-card__body) {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
}

.file-left {
  flex: 1 1 auto;
  min-width: 0;
}

.file-name-compact {
  font-weight: 800;
  color: #003831;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.file-actions {
  flex: 0 0 120px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: 2px;
}

.dl-link,
.dl-btn {
  color: #fffbf5 !important;
  background-color: #003831;
  height: 30px;
}

.dl-link,
.dl-btn:hover {
  color: #fffbf5 !important;
  background-color: #018575 !important;
}
</style>
